// src/services/multiplayerService.js - v2 - Includes REST Fallback & Connection Checks
import firebase, { auth, db, realtimeDB, firebaseConfig, isDatabaseConnected, forceReconnect } from '../firebaseConfig.js'; // Ensure firebaseConfig is exported
import { hasSpecialAccess, getSpecialAccessUserId, getSpecialAccessDisplayName, getSpecialAccessSessionId } from './specialAccessService.js';

console.log("Initializing multiplayer service...");
console.log("RealtimeDB initialized?", !!realtimeDB && typeof realtimeDB.ref === 'function');
const isProd = process.env.NODE_ENV === 'production'; // Assuming NODE_ENV is set during build
console.log(`Running in ${isProd ? 'PRODUCTION' : 'DEVELOPMENT'} mode`);
const databaseURL = firebaseConfig.databaseURL || 'URL_NOT_SET'; // Get URL from config
console.log(`Using database URL: ${databaseURL}`);


// Add connection listener for debugging
try {
    const connectedRef = realtimeDB.ref(".info/connected");
    connectedRef.on("value", (snap) => {
      if (snap.val() === true) {
        console.log("Firebase Realtime Database: Connected");
      } else {
        console.warn("Firebase Realtime Database: Disconnected");
      }
    }, (error) => {
         console.error("Error attaching connection listener:", error);
    });
} catch (e) {
     console.error("Failed to setup Firebase connection listener:", e);
}


/** Wait for Firebase Auth to initialize. */
function waitForAuth() {
  return new Promise((resolve, reject) => {
    if (auth.currentUser) return resolve(auth.currentUser);
    const unsubscribe = auth.onAuthStateChanged(user => { unsubscribe(); resolve(user); }, error => { unsubscribe(); reject(error); });
    setTimeout(() => { unsubscribe(); reject(new Error("Auth timeout")); }, 7000); // Increased timeout
  });
}

/** Ensure database connection, try reconnecting if needed. */
async function ensureDatabaseConnected() {
  const isConnected = await isDatabaseConnected();
  if (!isConnected) {
    console.log("Database not connected, attempting to reconnect...");
    try {
         forceReconnect(); // Attempt reconnect
         await new Promise(resolve => setTimeout(resolve, 2500)); // Wait longer
         const reconnected = await isDatabaseConnected();
         if (!reconnected) throw new Error("Failed to connect to game server after reconnect attempt.");
         console.log("Database reconnected successfully.");
    } catch (e) {
         console.error("Reconnection failed:", e);
         throw new Error("Failed to connect to game server. Please check your internet connection.");
    }
  }
  return true;
}

/** Generate a random 6-character game code (A-Z, 2-9). */
function generateGameCode() {
  const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let result = '';
  for (let i = 0; i < 6; i++) result += characters.charAt(Math.floor(Math.random() * characters.length));
  return result;
}

export async function createGameSession(userId, hostDisplayNameArg, gameMode = 'suddenDeath', timeLimit = 300) {
  const specialAccess = hasSpecialAccess();
  let isGuestPlus = false;
  let finalUserId = userId;
  let finalHostDisplayName = hostDisplayNameArg;

  if (!finalUserId && specialAccess) {
    console.log("MultiplayerService: Creating game for Guest+ user");
    finalUserId = getSpecialAccessUserId();
    finalHostDisplayName = getSpecialAccessDisplayName(); // Use Guest+ name
    isGuestPlus = true;
  }

  // Ensure auth state is potentially ready (doesn't strictly wait if Guest+)
  // await waitForAuth(); // Consider if needed even for Guest+ scenarios

  if (!finalUserId) throw new Error('User ID or Guest+ access is required to create a game');

  // --- ADDED: Fetch Display Name for Authenticated Users ---
  if (auth.currentUser && !isGuestPlus && finalUserId === auth.currentUser.uid) {
    try {
      const profileDoc = await db.collection("userProfiles").doc(finalUserId).get();
      if (profileDoc.exists && profileDoc.data().displayName) {
        finalHostDisplayName = profileDoc.data().displayName;
        console.log(`MultiplayerService: Fetched host displayName: ${finalHostDisplayName}`);
      } else {
        console.log(`MultiplayerService: Profile/displayName missing for host ${finalUserId}, using provided: ${finalHostDisplayName}`);
        if (!finalHostDisplayName) finalHostDisplayName = auth.currentUser.email || "Player"; // Ensure a name exists
      }
    } catch (err) {
      console.warn(`MultiplayerService: Error fetching host profile, using provided/fallback: ${finalHostDisplayName}`, err);
      if (!finalHostDisplayName) finalHostDisplayName = auth.currentUser.email || "Player";
    }
  } else if (!finalHostDisplayName) {
     // Ensure Guest+ or fallback has a name
     finalHostDisplayName = "Player";
  }
  // --- END ADDED ---

  try {
    await ensureDatabaseConnected();
  } catch (connErr) {
    console.warn("MultiplayerService: Proceeding with game creation despite connection check failure...");
    try { realtimeDB.goOnline(); } catch (e) {}
  }

  const gameCode = generateGameCode();
  const gameRef = realtimeDB.ref(`games/${gameCode}`);
  const now = Date.now(); // Use consistent local time for initial player object

  const gameData = {
    gameCode,
    hostId: finalUserId, // Use potentially updated finalUserId
    gameMode,
    timeLimit,
    status: 'waiting',
    createdAt: firebase.database.ServerValue.TIMESTAMP,
    lastUpdated: firebase.database.ServerValue.TIMESTAMP,
    isGuestPlusGame: isGuestPlus, // Correctly set Guest+ game flag
    players: {
      [finalUserId]: { // Use potentially updated finalUserId
        name: finalHostDisplayName, // Use potentially updated finalHostDisplayName
        isHost: true,
        joinedAt: now,
        status: 'waiting',
        score: 0,
        lives: 3,
        isAlive: true,
        position: { x: 0, y: 0 },
        isGuestPlus: isGuestPlus // Correctly set Guest+ player flag
      }
    },
    playerCount: 1
  };

  console.log(`MultiplayerService: Attempting to create game ${gameCode} with host ${finalUserId} (${finalHostDisplayName})`);
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
        console.error(`MultiplayerService: Game creation ${gameCode} timed out.`);
        reject(new Error('Game creation timed out. Please check connection and try again.'));
    }, 15000);

    gameRef.set(gameData, (error) => {
      clearTimeout(timeoutId);
      if (error) {
        console.error(`MultiplayerService: Error setting game data for ${gameCode}:`, error);
        reject(new Error(`Failed to create game: ${error.message}`));
      } else {
        console.log(`MultiplayerService: Game session created successfully: ${gameCode}`);
        resolve(gameCode);
      }
    });
  });
}

/**
 * Join an existing game by code.
 * Enhanced to support Guest+ users
 * @returns {Promise<void>}
 */
export async function joinGame(gameCode, userId, joinerDisplayNameArg) {
  const specialAccess = hasSpecialAccess();
  let isGuestPlus = false;
  let finalUserId = userId;
  let finalJoinerDisplayName = joinerDisplayNameArg;

  if (!finalUserId && specialAccess) {
      console.log("MultiplayerService: Joining game as Guest+ user");
      finalUserId = getSpecialAccessUserId();
      finalJoinerDisplayName = getSpecialAccessDisplayName();
      isGuestPlus = true;
  }

  if (!gameCode || !finalUserId) throw new Error('Game code and user ID/Guest+ access are required');

  try {
      await ensureDatabaseConnected();
  } catch (connErr) {
      console.warn("MultiplayerService: Proceeding with join despite connection check failure...");
      try { realtimeDB.goOnline(); } catch (e) { }
  }

  // Fetch Display Name (Keep this logic)
  if (auth.currentUser && !isGuestPlus && finalUserId === auth.currentUser.uid) {
      try {
          const profileDoc = await db.collection("userProfiles").doc(finalUserId).get();
          if (profileDoc.exists && profileDoc.data().displayName) {
              finalJoinerDisplayName = profileDoc.data().displayName;
          } else if (!finalJoinerDisplayName) {
              finalJoinerDisplayName = auth.currentUser.email || "Player";
          }
      } catch (err) {
          console.warn(`MultiplayerService joinGame: Error fetching profile`, err);
          if (!finalJoinerDisplayName) finalJoinerDisplayName = auth.currentUser.email || "Player";
      }
  } else if (!finalJoinerDisplayName) {
      finalJoinerDisplayName = "Player";
  }
  // --- End Fetch Display Name ---

  const gameRef = realtimeDB.ref(`games/${gameCode}`);
  const playerRef = realtimeDB.ref(`games/${gameCode}/players/${finalUserId}`); // Reference to the specific player slot
  const now = Date.now();

  const playerData = {
      name: finalJoinerDisplayName, isHost: false, joinedAt: now,
      status: 'waiting', score: 0, lives: 3, isAlive: true,
      position: { x: 0, y: 0 },
      // Use server time for lastUpdated, but it will be set AFTER the transaction
      // lastUpdated: firebase.database.ServerValue.TIMESTAMP,
      isGuestPlus: isGuestPlus
  };

  try {
      // --- Pre-check (Keep this) ---
      const initialSnapshot = await gameRef.once('value');
      if (!initialSnapshot.exists()) {
          throw new Error('Game not found.');
      }
      const initialGameData = initialSnapshot.val();
      if (initialGameData.status !== 'waiting') {
          throw new Error('Game has already started or ended.');
      }
      // --- End Pre-check ---

      // --- *** MODIFIED TRANSACTION: ONLY Write to playerRef *** ---
      // Transaction now targets the specific player path.
      // This ensures the transaction only modifies data the user *should* have permission for.
      const result = await playerRef.transaction(currentPlayerData => {
          if (currentPlayerData === null) {
              // Player slot is empty, OK to write.
              // Add the lastUpdated field here, it will be part of the player data written.
              return { ...playerData, lastUpdated: firebase.database.ServerValue.TIMESTAMP };
          } else {
              // Player slot is NOT empty, abort the transaction.
              console.warn(`[Transaction] Player slot for ${finalUserId} is not empty. Aborting write.`);
              return undefined; // Abort the transaction
          }
      });
      // --- *** END MODIFIED TRANSACTION *** ---

      // --- Post-Transaction Validation ---
      if (!result.committed) {
          // Transaction aborted, likely because the player slot was already filled (currentPlayerData wasn't null).
          console.warn(`MultiplayerService: Join transaction aborted for ${finalUserId} in ${gameCode}. Player likely already exists.`);
          // Verify if the player actually exists now (could be a race condition)
          const checkSnap = await playerRef.once('value');
          if (checkSnap.exists()) {
              console.log(`MultiplayerService: Join successful (Player ${finalUserId} already present).`);
              // Optionally update lastSeen if needed, though their own state updates will do this.
              // await playerRef.update({ lastSeen: firebase.database.ServerValue.TIMESTAMP });
          } else {
               // This case is now less likely unless there's a rule issue preventing even the initial read/write attempt
              console.error("[Transaction Aborted] Player slot was expected to be filled, but isn't. Check security rules for player path write access.");
              throw new Error('Failed to join game. Player slot conflict or rule issue.');
          }
      } else {
          // Transaction committed successfully - player data was written
          console.log(`MultiplayerService: User ${finalUserId} (${finalJoinerDisplayName}) successfully added to game ${gameCode}.`);
           // Optional: Trigger a game update if needed (e.g., update host's game timestamp, but this might require separate permissions or Cloud Function)
           // Example: gameRef.update({ lastUpdated: firebase.database.ServerValue.TIMESTAMP }); // Needs appropriate rules
      }

  } catch (error) {
      console.error(`MultiplayerService: Error joining game ${gameCode}:`, error);
      // Rethrow specific errors for JoinGameScene to handle
      if (error.message.includes('Game not found')) {
          throw new Error('Game not found.');
      } else if (error.message.includes('already started or ended')) {
          throw new Error('Game has already started or ended.');
      } else if (error.message.includes('Player slot conflict or rule issue')) {
           throw error; // Pass the specific error message
      }
      // Throw a generic error for other issues
      throw new Error('Failed to join the game. Please check the code and try again.');
  }
}
// --- END REPLACE joinGame FUNCTION ---


/**
 * Start a game (host only).
 * @returns {Promise<void>}
 */
export async function startGame(gameCode, userId, forceSinglePlayer = false) {
  // await waitForAuth(); // Auth check happens before calling this usually
  if (!gameCode || !userId) throw new Error('Game code and user ID are required');

  try { // Add try-catch around the whole operation
    await ensureDatabaseConnected();

    const gameRef = realtimeDB.ref(`games/${gameCode}`);
    const snapshot = await gameRef.once('value');

    if (!snapshot.exists()) {
      throw new Error('Game not found.');
    }
    const gameData = snapshot.val();

    if (gameData.hostId !== userId) {
      throw new Error('Only the host can start the game.');
    }
    if (gameData.status !== 'waiting') {
      // Game might have been started by another client instance or due to listener delay
      console.warn(`Attempted to start game ${gameCode} but status is already ${gameData.status}.`);
      // Optionally return gracefully or throw a specific error
      // For now, let's throw to indicate the state mismatch clearly.
      throw new Error('Game already started or ended.');
    }

    // --- *** MODIFICATION: Count players directly *** ---
    const actualPlayerCount = gameData.players ? Object.keys(gameData.players).length : 0;
    console.log(`startGame Check: Actual players found in DB: ${actualPlayerCount}`);

    // Use the actualPlayerCount for the check, unless forceSinglePlayer is true
    if (!forceSinglePlayer && actualPlayerCount < 2) {
        console.log(`Start prevented: actualPlayerCount (${actualPlayerCount}) < 2 and forceSinglePlayer is false.`);
        throw new Error('Need at least 2 players to start.');
    }
    // --- *** END MODIFICATION *** ---

    console.log(`Starting game: ${gameCode} (Forced: ${forceSinglePlayer}, Actual Players: ${actualPlayerCount})`);

    // Update game status and add startedAt timestamp
    // Also, update the playerCount here based on the actual count found
    await gameRef.update({
      status: 'playing',
      startedAt: firebase.database.ServerValue.TIMESTAMP,
      lastUpdated: firebase.database.ServerValue.TIMESTAMP,
      playerCount: actualPlayerCount // Update playerCount to the correct value
    });

    console.log(`Game ${gameCode} status updated to 'playing'.`);

  } catch (error) {
      console.error(`Error starting game ${gameCode}:`, error);
      // Re-throw the error so the calling scene (LobbyScene) can handle it
      throw error;
  }
}

// --- END REPLACE startGame FUNCTION ---

/**
 * Update a player's state during gameplay.
 * Enhanced to support Guest+ users
 * @param {string} gameCode - The game code.
 * @param {string} userId - The user ID.
 * @param {Object} stateData - Data like score, lives, position, etc.
 * @returns {Promise<void>}
 */

// --- REPLACE updatePlayerState FUNCTION ---
export async function updatePlayerState(gameCode, userId, stateData) {
  const specialAccess = hasSpecialAccess();
  let finalUserId = userId;
  let isGuestPlusUser = false;

  if (!finalUserId && specialAccess) {
    finalUserId = getSpecialAccessUserId();
    isGuestPlusUser = true;
  }

  if (!gameCode || !finalUserId || !stateData) {
    return; // Silent fail
  }

  if (isGuestPlusUser) {
    // --- GUEST+ PATH: Use Server-Side API ---
    const sessionId = getSpecialAccessSessionId();
    if (!sessionId) {
        console.error("Guest+ updatePlayerState: Missing session ID. Cannot update state.");
        return;
    }

    // Prepare data for API (DO NOT include ServerValue.TIMESTAMP here)
    const apiStateData = { ...stateData };
    delete apiStateData.lastUpdated; // Server will add its own timestamp

    try {
         // console.log(`Guest+ Sending state update via API for ${finalUserId}`); // Reduce log noise
         const response = await fetch('/api/game/updateGuestState', { // Ensure correct endpoint path
             method: 'POST',
             headers: { 'Content-Type': 'application/json' },
             body: JSON.stringify({
                 sessionId: sessionId,
                 gameCode: gameCode,
                 userId: finalUserId,
                 stateData: apiStateData // Send sanitized data
             })
         });

         if (!response.ok) {
             const errorData = await response.json().catch(() => ({ error: "Unknown API error" }));
             console.error(`API Error updating Guest+ state (${response.status}):`, errorData.error);
             // Handle specific errors like 403 (Invalid Session) if needed
             if (response.status === 403) {
                  // Maybe trigger UI update or session cleanup
             }
         } else {
             // console.log(`Guest+ state update successful via API for ${finalUserId}`); // Reduce log noise
         }
    } catch (error) {
        console.error(`Network Error updating Guest+ state via API for ${finalUserId}:`, error);
    }

  } else {
    // --- AUTHENTICATED USER PATH: Use RTDB directly ---
    try {
      const playerRef = realtimeDB.ref(`games/${gameCode}/players/${finalUserId}`);
      const updatePayload = {
        ...stateData,
        lastUpdated: firebase.database.ServerValue.TIMESTAMP,
        isGuestPlus: false // Ensure this is false for authenticated users
      };
      await playerRef.update(updatePayload);
      // console.log(`RTDB: Updated state for ${finalUserId} in ${gameCode}`); // Reduce log noise
    } catch (error) {
      console.error(`RTDB Error updating player state for ${finalUserId} in ${gameCode}:`, error);
    }
  }
}

/**
 * Listen for changes to a game. Handles PERMISSION_DENIED.
 * @returns {Function} Unsubscribe function.
 */
export function listenToGameUpdates(gameCode, callback) {
    if (!gameCode || typeof callback !== 'function') {
        console.error('listenToGameUpdates: Game code and callback function are required');
        return () => {}; // Return no-op unsubscribe
    }

    console.log(`Setting up listener for game: ${gameCode}`);
    const gameRef = realtimeDB.ref(`games/${gameCode}`);
    let initialDataLoaded = false;

    const onValueChange = (snapshot) => {
        if (!snapshot.exists()) {
            console.warn(`Listener Warning: No data found for game ${gameCode}. Game might have ended or been deleted.`);
            // Only call back null if initial data wasn't loaded, otherwise might be game end
            if (!initialDataLoaded) {
                callback(null);
            } else {
                 // Can optionally signal game end here if needed
                 callback({ status: 'ended', reason: 'deleted' });
            }
        } else {
            initialDataLoaded = true;
            const data = snapshot.val();
            callback(data);
        }
    };

    const onError = (error) => {
        console.error(`Firebase listener error for game ${gameCode}:`, error);
        // Handle specific errors
        if (error.code === 'PERMISSION_DENIED') {
             console.error(`PERMISSION DENIED for game ${gameCode}. User might need to re-authenticate.`);
            callback({ error: "PERMISSION_DENIED", message: "Access denied. Please try signing in again." });
             // Detach listener on permission denied
             gameRef.off('value', onValueChange);
        } else {
            // Attempt to force reconnect on other errors
             console.log("Attempting reconnect after listener error...");
             forceReconnect(); // Try reconnecting
             callback({ error: "LISTENER_ERROR", message: "Connection issue. Attempting to reconnect..." });
        }
    };

    // Attach listener
    gameRef.on('value', onValueChange, onError);

    // Return the unsubscribe function
    return () => {
        console.log(`Removing listener for game: ${gameCode}`);
        gameRef.off('value', onValueChange); // Detach the specific listener function
    };
}


/**
 * Check if a game code exists.
 * @returns {Promise<boolean>}
 */
export async function checkGameExists(gameCode) {
  if (!gameCode) return false;
  try {
    await ensureDatabaseConnected();
    const gameRef = realtimeDB.ref(`games/${gameCode}`);
    const snapshot = await gameRef.once('value');
    return snapshot.exists();
  } catch (error) {
    console.error(`Error checking if game ${gameCode} exists:`, error);
    return false; // Assume not found on error
  }
}

// --- REPLACE leaveGame FUNCTION ---
export async function leaveGame(gameCode, userId) {
  const specialAccess = hasSpecialAccess();
  let finalUserId = userId;

  if (!finalUserId && specialAccess) {
    finalUserId = getSpecialAccessUserId();
  }

  // await waitForAuth(); // Not strictly needed if we have finalUserId
  if (!gameCode || !finalUserId) {
     console.warn("MultiplayerService: Skipping leaveGame - missing data.");
     return;
  }

  try {
    await ensureDatabaseConnected(); // Ensure connection before modifying data
  } catch (connErr) {
     console.error("MultiplayerService: Cannot leave game - connection error.", connErr);
     // Decide how to handle - maybe just return false or throw?
     // For now, log and exit, assuming client will navigate away anyway.
     return;
  }


  try {
    const gameRef = realtimeDB.ref(`games/${gameCode}`);
    const playerRef = realtimeDB.ref(`games/${gameCode}/players/${finalUserId}`);

    // Use a transaction to safely check game state and player existence
    const result = await gameRef.transaction(currentGameData => {
      if (currentGameData === null) {
        return undefined; // Abort if game doesn't exist
      }

      // Check if player exists in the game
      if (!currentGameData.players || !currentGameData.players[finalUserId]) {
        console.warn(`MultiplayerService: User ${finalUserId} not found in game ${gameCode} during leave attempt.`);
        return currentGameData; // Abort modifications, but don't nullify game
      }

      if (currentGameData.hostId === finalUserId) {
        // Host leaves - end the game
        console.log(`MultiplayerService: Host ${finalUserId} leaving game ${gameCode}. Ending game.`);
        currentGameData.status = 'ended';
        currentGameData.endedAt = firebase.database.ServerValue.TIMESTAMP;
        currentGameData.lastUpdated = firebase.database.ServerValue.TIMESTAMP;
        currentGameData.endReason = 'host_left';
        // Optionally nullify players or keep them for record? Keeping them for now.
      } else {
        // Non-host player leaves
        console.log(`MultiplayerService: Player ${finalUserId} leaving game ${gameCode}.`);
        // Remove player data
        delete currentGameData.players[finalUserId];
        // Decrement player count safely
        currentGameData.playerCount = Math.max(0, (currentGameData.playerCount || 1) - 1);
        currentGameData.lastUpdated = firebase.database.ServerValue.TIMESTAMP;
      }
      return currentGameData;
    });

    if (result.committed) {
         console.log(`MultiplayerService: Leave game transaction committed for ${finalUserId} in ${gameCode}.`);
    } else {
         console.warn(`MultiplayerService: Leave game transaction aborted for ${finalUserId} in ${gameCode}. Game state might have changed.`);
         // Check final state again if needed
         const finalSnap = await gameRef.once('value');
         if(!finalSnap.exists() || (finalSnap.val().players && !finalSnap.val().players[finalUserId])){
             console.log(`MultiplayerService: Player ${finalUserId} successfully removed or game ended.`);
         } else {
              console.error(`MultiplayerService: Failed to remove player ${finalUserId} from game ${gameCode}.`);
         }
    }

  } catch (error) {
    console.error(`MultiplayerService: Error leaving game ${gameCode} for user ${finalUserId}:`, error);
    // Don't throw to avoid blocking UI/navigation, but log severity
  }
}
 

// Fallback method using REST API (If needed, less critical now with connection checks)
// export async function createGameSessionWithRESTFallback(...) { ... }

export default {
  createGameSession,
  joinGame,
  startGame,
  updatePlayerState,
  listenToGameUpdates,
  leaveGame,
  checkGameExists,
  // createGameSessionWithRESTFallback // Uncomment if used
};